import {
  getCart,
  formatMoney,
  updateCart,
  getProduct,
  getMetafields,
  removeFromCart,
  addToCartMultiple,
  cartOperationManager
} from './_global-functions.js';

class Cart extends HTMLElement {
  constructor() {
    super();

    if (this.closest('[data-template="cart"]')) {
      this.classList.add('cart-page-component');
      this.classList.remove('cart--closed');
    }

    // Cart
    this.cart;

    // Templates
    this.cartContainer;
    this.cartHeadings;
    this.cartItems;
    this.cartTotals;
    this.cartItemTemplate;
    this.cartStatus;
    this.cartItemsMetafields;
    
    // Globals
    window.cartItemsMetafields = window.cartItemsMetafields || {};

    // Totals
    this.cartTotalItems;
    this.cartTotalPrice;

    // Per-item quantity update state: latest-wins queue
    // Map<itemKey, { inFlight: boolean, pending: number|null }>
    this.quantityUpdateState = new Map();

    // CSS empty-state hooks
    this.isEmpty = null; // null means uninitialized
  }

  async connectedCallback() {
    if(window.location.pathname.includes('/cart') && this.parentElement.id.includes('shopify-section-cart')) {
      return;
    }

    // Set up the cart
    this.cartContainer = this.querySelector('.cart-container');
    this.cartHeadings = this.querySelector('[cart-headings]');
    this.cartItems = this.querySelector('[cart-items]');
    this.cartTotals = this.querySelector('[cart-totals]');
    this.cartStatus = this.getAttribute('cart-status');

    // Cart templates
    this.cartItemTemplate = document.querySelector('template#cart-item');
    this.cartEmpty = document.querySelector('template#cart-empty');
    this.cartTotalPrice = this.querySelector('[cart-total-price]');
    this.cartTotalItems = this.querySelector('[cart-total-items]');

    this.cartClose = document.querySelectorAll('[cart-close]');
    this.cartOpen = document.querySelectorAll('[cart-open]');
    this.cartToggle = document.querySelectorAll('[cart-toggle]');
    this.cartToggleCount = document.querySelector('[cart-count]');
    this.cartAtcTrigger = document.querySelectorAll('[cart-atc-trigger]');


    this.cartToggleEvents();
    this.cartEvents();

    // Initial load
    if (this.cartStatus === 'initial' || this.cartStatus === 'loaded' || !this.cartStatus) {
      this.loadCart();
    }
  }

  cartEvents() {
    // Listen for add-to-cart events: refresh and open cart
    document.addEventListener('add-to-cart', () => {
      this.setStatus('updating');
      this.openCart();
    });

    // Listen for update-cart events: use provided cart to avoid an extra fetch
    document.addEventListener('update-cart', async (e) => {
      if (e?.detail?.cart) {
        this.cart = e.detail.cart;
        this.setStatus('loaded');
        // Only fetch metafields if cart has items
        if (this.cart.items && this.cart.items.length > 0) {
          this.cartItemsMetafields = await this.getMetafields() ?? false;
        } else {
          this.cartItemsMetafields = false;
        }
        this.renderCart();
      } else {
        this.setStatus('updating');
      }
    });

    // Listen for cart operation start/end to disable/enable buttons
    document.addEventListener('cart-operation-start', (e) => {
      this.setAllCartButtonsDisabled(true);
    });

    document.addEventListener('cart-operation-end', (e) => {
      this.setAllCartButtonsDisabled(false);
    }); 
  }

  cartToggleEvents() {
    this.cartClose?.forEach(el => {
      el.addEventListener('click', () => this.closeCart());
    });

    this.cartOpen?.forEach(el => {
      el.addEventListener('click', () => this.openCart());
    });

    this.cartToggle?.forEach(el => {
      el.addEventListener('click', () => {
        this.toggleCart();
      });
    });

    // Close when clicking the overlay (mini-cart::before)
    this.addEventListener('click', (e) => {
      if (!this.classList.contains('cart--open')) return;
      
      // Don't close if clicking on variant selector elements
      const isVariantElement = e.target.closest('.variant-selector') || 
                              e.target.closest('.variant-atc') ||
                              e.target.classList.contains('variant-selector__label') ||
                              e.target.classList.contains('variant-selector__option') ||
                              e.target.classList.contains('variant-atc');
      
      if (isVariantElement) return;
      
      const panel = this.querySelector('.cart-container');
      if (panel && !panel.contains(e.target)) {
        this.closeCart();
      }
    });

    // Add to cart triggers
    this.cartAtcTrigger?.forEach(el => {
      el.addEventListener('click', async (e) => {
        try {
          await this.handleAddToCartTrigger(e);
        } catch (err) {
          console.error('Failed handleAddToCartTrigger:', err);
        }
      });
    });
  }

  closeCart() {
    if (this.closest('[data-template="cart"]')) {
      return;
    }
    
    document.querySelector('html').style.overflow = 'auto';
    // Keep stacking above header while animating out, then finalize close
    this.classList.remove('cart--open');
    this.classList.add('cart--closing');
    const onDone = (e) => {
      // Only react to the transform transition of the panel
      if (e && e.target !== this.querySelector('.cart-container')) return;
      this.classList.remove('cart--closing');
      this.classList.add('cart--closed');
      this.removeEventListener('transitionend', onDone);
    };
    this.addEventListener('transitionend', onDone);
    // Safety fallback in case transition event is missed
    setTimeout(onDone, 600);
  }

  openCart() {
    if (this.closest('[data-template="cart"]')) {
      return;
    }
    
    // lock html scroll
    document.querySelector('html').style.overflow = 'hidden';
    this.classList.remove('cart--closed');
    this.classList.add('cart--open');
    // Clear inline z-index set on close so CSS class can control stacking
    this.style.zIndex = '';


    // Add event listener for add-to-cart-slider
    document.dispatchEvent(new CustomEvent('add-to-cart-slider', {
      detail: {
        status: 'reload'
      }
    }));
  }

  toggleCart() {
    if (this.closest('[data-template="cart"]')) {
      return;
    }
    
    document.querySelector('html').style.overflow = this.classList.contains('cart--closed') ? 'hidden' : 'auto';
    this.classList.toggle('cart--open');
    this.classList.toggle('cart--closed');
  }

  async initCart() {
    // Get the cart
    const cart = await this.loadCart();
    if (cart) this.renderCart(cart);
  }

  async loadCart() {
    // Prevent duplicate calls
    if (this.cartStatus === 'loading') {
      return this.cart;
    }
    
    try {
      this.setStatus('loading');
      this.cart = await getCart();
      // Only fetch metafields if cart has items
      if (this.cart.items && this.cart.items.length > 0) {
        this.cartItemsMetafields = await this.getMetafields() ?? false;
      } else {
        this.cartItemsMetafields = false;
      }
      this.setStatus('loaded');
      return this.cart;
    } catch (err) {
      console.error('Failed to load cart:', err);
      return null;
    }
  }

  async getMetafields(type = 'product', metafieldNames = null) {
    // Validate cart and cart items exist before proceeding
    if (!this.cart || !this.cart.items || !Array.isArray(this.cart.items) || this.cart.items.length === 0) {
      console.warn('Cart or cart items not available for metafields fetch:', {
        cart: this.cart,
        items: this.cart?.items
      });
      return false;
    }

    if(metafieldNames === null) {
      metafieldNames = this.dataset.metafields.split(',');
    }

    const productIds = this.cart.items.map(item => item.product_id);
    const metafields = await getMetafields(type, productIds, metafieldNames);

    // const formattedMetafields = await this.formatMetafields(metafields, metafieldName);
    return metafields;
  }

  // async formatMetafields(metafields) {
  //   const formattedMetafields = {};
    
  //   for (const product of metafields) {
  //     const { handle, metafields: productMetafields } = product;
      
  //     // Initialize the product handle object if it doesn't exist
  //     if (!formattedMetafields[handle]) {
  //       formattedMetafields[handle] = {};
  //     }
      
  //     // Process each metafield for this product
  //     for (const metafield of productMetafields) {
  //       const { namespace, key, value, type } = metafield;
  //       const metafieldKey = `${namespace}.${key}`;
        
  //       let processedValue = value;
        
  //       // Convert JSON strings to objects for image and video types
  //       if (type === 'json_string' && (key.includes('image') || key.includes('video') || namespace.includes('image') || namespace.includes('video'))) {
  //         try {
  //           processedValue = JSON.parse(value);
  //         } catch (error) {
  //           console.warn(`Failed to parse JSON for metafield ${metafieldKey}:`, error);
  //           processedValue = value; // Keep original value if parsing fails
  //         }
  //       }
        
  //       // Handle cart.cart_redirect: value is a product handle, get that product's URL
  //       if (metafieldKey === 'cart.cart_redirect') {
  //         try {
  //           const redirectProduct = await getProduct(value); // value is the redirect product handle
  //           processedValue = redirectProduct?.url || value; // Get the redirect product's URL
  //         } catch (error) {
  //           console.warn(`Failed to get redirect product for handle ${value}:`, error);
  //           processedValue = value; // Keep original value if product fetch fails
  //         }
  //       }

  //       formattedMetafields[handle][metafieldKey] = {
  //         value: processedValue
  //       };
  //     }
  //   }

  //   // Filter by the metafields specified in the data-metafields attribute
  //   const dataMetafieldsAttr = this.getAttribute('data-metafields');
  //   if (dataMetafieldsAttr) {
  //     const requiredMetafields = dataMetafieldsAttr.split(',').map(field => field.trim());
      
  //             // Filter formattedMetafields to only include the required ones
  //       const filteredMetafields = {};
  //       Object.keys(formattedMetafields).forEach( handle => {
  //         filteredMetafields[handle] = {};
  //         requiredMetafields.forEach(async requiredField => {
  //           // Handle special URL: prefix case
  //           if (requiredField.startsWith('URL:')) {
  //             const cleanFieldName = requiredField.replace('URL:', '').trim();
  //             if (formattedMetafields[handle][cleanFieldName]) {
  //               filteredMetafields[handle][cleanFieldName] = formattedMetafields[handle][cleanFieldName];
  //             }
  //           } else if (formattedMetafields[handle][requiredField]) {
  //             filteredMetafields[handle][requiredField] = formattedMetafields[handle][requiredField];
  //           }
  //         });
  //       });
      
  //     return filteredMetafields;
  //   }
    
  //   return formattedMetafields;
  // }

  setStatus(status) {
    this.cartStatus = status;
    this.setAttribute('cart-status', this.cartStatus);
  }

  renderCart(cart = this.cart) {
    if(cart) {
      const count = cart.item_count || 0;
      // if(this.cartToggleCount) {
      //   this.cartToggleCount.innerHTML = count !== 0 ? count : '';
      //   this.cartToggleCount.setAttribute('cart-count', count);
      // }

      if(this.cartTotalPrice && cart.total_price) 
        this.cartTotalPrice.innerHTML = `<span>Subtotal</span><span>${this.getPriceFormat(cart.total_price)}</span>`
      // Update empty state class/attribute for CSS hooks
      this.updateEmptyState(cart);

      if(count === 0) {
        this.transitionToEmpty();
      } else {
        this.querySelectorAll('.cart-empty-item').forEach(item => item.remove());
        this.rebuildCartItems(cart.items);
        // Scroll to top of cart items when items are present
        this.scrollToTopItems();
      }
    }
  }

  transitionToEmpty() {
    // Preserve height temporarily to prevent layout shift
    const currentHeight = this.cartItems.scrollHeight;
    this.cartItems.style.height = currentHeight + 'px';
    
    // Remove only cart items, preserve the slider
    const cartItemElements = this.cartItems.querySelectorAll('[cart-item]');
    cartItemElements.forEach(item => item.remove());
    
    // Add empty state content
    const emptyStateContent = this.cartEmpty.content.cloneNode(true);
    this.cartItems.innerHTML = '';
    this.cartItems.appendChild(emptyStateContent);
    
    // Remove height after a frame to allow natural sizing
    requestAnimationFrame(() => {
      this.cartItems.style.height = '';
    });
  }

  capitalizeFirstLetter(string) {
    return string.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  rebuildCartItems(items) {
    // Preserve height temporarily to prevent layout shift
    const currentHeight = this.cartItems.scrollHeight;
    this.cartItems.style.height = currentHeight + 'px';
    
    // Build new content off-screen first
    const tempContainer = document.createElement('div');
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'absolute';
    this.cartItems.parentNode.appendChild(tempContainer);
    
    items.forEach((item, index) => {
          const itemFrag = this.cartItemTemplate.content.cloneNode(true);
          
          // Base info
          // Safely access metafields
          const itemMetafields = this.cartItemsMetafields?.data?.length > 0 ? this.cartItemsMetafields.get(this.dataset.metafields.split(','), null, index) : null;
          const metaTitle = itemMetafields?.[this.getAttribute('data-title-key')];
          const metaSubtitle = itemMetafields?.[this.getAttribute('data-subtitle-key')];
          const metaMainImage = JSON.parse(itemMetafields?.[this.getAttribute('data-main-image-key')])?.[0]?.src;
          const metaCartRedirect = itemMetafields?.[this.getAttribute('data-cart-redirect-key')];

          let url = metaCartRedirect || item.url;
          if(Shopify.country !== 'US' && !url.includes('en-')) {
            url = `/en-${Shopify.country}${url}`
          }

          itemFrag.querySelectorAll('a').forEach(a => a.href = url);
          const rootEl = itemFrag.querySelector('[cart-item]');
          rootEl.setAttribute('cart-item', item.id);
          rootEl.setAttribute('data-key', item.key);
          itemFrag.querySelector('.cart-item--image').src = item.product_has_only_default_variant 
            ? metaMainImage || item.image : item.image;
          itemFrag.querySelector('.cart-item--title').innerHTML = metaTitle?.value || item.product_title;
          itemFrag.querySelector('.cart-item--subtitle').innerHTML = metaSubtitle?.value || '';
          
          if(metaTitle?.value?.includes('J2') 
              || metaTitle?.value?.includes('J3') 
              || metaTitle?.value?.includes('J1') 
              || metaTitle?.value?.includes('M1') 
              || metaTitle?.value?.includes('C2')) {
            itemFrag.querySelector('.cart-item--subtitle').innerHTML = '';
          }

          const propsEl = itemFrag.querySelector('.cart-item--properties');
          if (propsEl) propsEl.innerHTML = this.formatItemProperties(item);

          const title = itemFrag.querySelector('.cart-item--title');
          const subtitle = itemFrag.querySelector('.cart-item--subtitle');
          if(title) title.innerHTML = this.capitalizeFirstLetter(title.innerHTML);
          if(subtitle) subtitle.innerHTML = this.capitalizeFirstLetter(subtitle.innerHTML);

          // Prices (respect discounts and quantity)
          const itemPrice = itemFrag.querySelector('.cart-item--price');
          const priceFinal = itemPrice?.querySelector('.cart-item--price-value');
          const priceDiscount = itemPrice?.querySelector('.cart-item--price-discount');
          if (priceFinal) {
            if (item.original_price !== item.final_price && priceDiscount) {
              priceDiscount.innerHTML = this.getPriceFormat(item.original_price * item.quantity);
              priceFinal.innerHTML = this.getPriceFormat(item.final_price * item.quantity);
            } else {
              priceFinal.innerHTML = this.getPriceFormat(item.price * item.quantity);
            }
          }

          // Quantity controls
          const qtyWrap = itemFrag.querySelector('.cart-item--quantity');
          const qtyInput = qtyWrap?.querySelector('input');
          const decBtn = qtyWrap?.querySelector('.cart-item--quantity-decrement');
          const incBtn = qtyWrap?.querySelector('.cart-item--quantity-increment');
          if (qtyInput) qtyInput.value = item.quantity;
          const updateTo = (newQty) => {
            this.scheduleQuantityUpdate(item, newQty, rootEl);
          };
          decBtn?.addEventListener('click', () => {
            const newQty = Math.max(0, parseInt(qtyInput.value || '1', 10) - 1);
            updateTo(newQty);
          });
          if(item.requires_shipping === false || priceFinal.innerHTML === 'FREE') {
            incBtn.style.opacity = 0.5;
            incBtn.style.cursor = 'not-allowed';
          }
          incBtn?.addEventListener('click', () => {
            if(item.requires_shipping === false || priceFinal.innerHTML === 'FREE') return;
            const newQty = Math.max(1, parseInt(qtyInput.value || '1', 10) + 1);
            updateTo(newQty);
          });
          qtyInput?.addEventListener('change', (e) => {
            const v = parseInt(e.target.value, 10);
            if (Number.isNaN(v) || v < 1) {
              e.target.value = 1;
              updateTo(1);
            } else {
              updateTo(v);
            }
          });

          // Remove button
          itemFrag.querySelector('.cart-item--remove')?.addEventListener('click', async () => {
            await this.removeFromCart(item);
          });

          tempContainer.appendChild(itemFrag);
        });

        // Add to cart slider (only if it doesn't already exist)
        const existingSliderContainer = this.cartItems.querySelector('.mini-cart--slider');
        if (!existingSliderContainer) {
          // Create new slider container if it doesn't exist (first time only)
          const cartSlider = document.querySelector('template#cart-slider');
          const cartSliderFrag = cartSlider.content.cloneNode(true);
          tempContainer.appendChild(cartSliderFrag);
        } else {
          // IMPORTANT: Don't move existing slider to prevent disconnection/reconnection
          // Leave it in place to avoid layout shifts and slider reinitialization
          // The slider will update itself via events, no need to move the DOM element
        }

    // CRITICAL: Preserve existing slider to prevent DOM disconnection/reconnection
    const existingSliderToPreserve = this.cartItems.querySelector('.mini-cart--slider');
    
    // Remove only cart items, NOT the slider
    const cartItemElements = this.cartItems.querySelectorAll('[cart-item]');
    cartItemElements.forEach(item => item.remove());
    
    // Add new cart items BEFORE the slider (if it exists)
    if (existingSliderToPreserve) {
      // Insert cart items before the slider
      while (tempContainer.firstChild) {
        this.cartItems.insertBefore(tempContainer.firstChild, existingSliderToPreserve);
      }
    } else {
      // No slider exists, just append normally
      while (tempContainer.firstChild) {
        this.cartItems.appendChild(tempContainer.firstChild);
      }
    }
    
    // Slider remains untouched and in correct position!
    
    // Clean up temp container and restore natural height
    tempContainer.remove();
    requestAnimationFrame(() => {
      this.cartItems.style.height = '';
    });
  }

  async removeFromCart(item) {
    const parent = this.cartItems.querySelector(`[cart-item="${item.id}"]`);
    parent?.classList.add('cart-item--removing');
    try {
      const cart = await removeFromCart(item.key);
      if (cart) {
        this.cart = cart;
        this.setStatus('loaded');
        this.updateEmptyState(cart);
        this.renderCart(cart);
      }
    } catch (err) {
      console.error('Failed to remove from cart:', err);
    } finally {
      parent?.classList.remove('cart-item--removing');
    }
  }

  getPriceFormat(price) {
    if(price === 0) {
      return 'FREE';
    }
    // return `${Shopify.scFormatMoney(price)} ${Shopify.country === 'US' ? '' : Shopify.currency.active}`;
    const currency = Shopify.currency.active;
    const locale = Shopify.locale;
    return `${formatMoney(price, currency, locale)} ${Shopify.country === 'US' ? '' : Shopify.currency.active}`;
  }

  static get observedAttributes() {
    // returns an array of attributes that the element can observe
    return ['cart-status'];
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if(name === 'cart-status') {
      if (newValue === 'initial') {
        await this.loadCart();
      }

      if (oldValue !== 'initial' && newValue === 'loaded') {
        this.renderCart();
      }

      if (newValue === 'updating') {
        this.loadCart();
      }
    }
  }

  // there can be other element methods and properties
  
  // Minimal property formatter with Combined Listings child support
  updateEmptyState(cart = this.cart) {
    const isEmpty = !cart || (cart.item_count || 0) === 0;
    const wasEmpty = this.isEmpty;
    const isFirstLoad = this.isEmpty === null;
    this.isEmpty = isEmpty;
    this.classList.toggle('cart--empty', isEmpty);

    if(this.isEmpty) {
      document.dispatchEvent(new CustomEvent('add-to-cart-slider', {
        detail: {
          status: 'empty'
        }
      }));
    } else {
      // Cart has items - always reload slider (covers all cases: empty->items, items->more items, first load with items)
      document.dispatchEvent(new CustomEvent('add-to-cart-slider', {
        detail: {
          status: 'reload'
        }
      }));
    }
  }

  formatItemProperties(item) {
    // Prefer options_with_values; fallback to properties or variant_options
    const options = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
    const props = item?.properties && Object.keys(item.properties).length ? item.properties : null;
    const variantOptions = Array.isArray(item?.variant_options) ? item.variant_options : [];

    // Combined Listings child: derive plug type from product title
    if (this.isCombinedListingChild(item)) {
      return this.formatCombinedListingChildProperties(item);
    }

    const parts = [];

    if (options.length) {
      const color = options.find(o => o.name?.toLowerCase().includes('color'))?.value;
      if (color) parts.push(color);
      
      // Check for plug-type in handle if only one option
      if (options.length === 1 && item?.handle?.includes('plug-type')) {
        const handle = item.handle;
        const plugTypeMatch = handle.match(/plug-type-([a-z])/i);
        if (plugTypeMatch) {
          const plugLetter = plugTypeMatch[1].toUpperCase();
          parts.push(`Plug Type ${plugLetter}`);
        }
      }
      
      options.forEach(o => {
        if (!o?.name?.toLowerCase().includes('color')) {
          const v = o.value || '';
          if (v.toLowerCase().includes('type')) parts.push(`Plug ${v}`);
          else if (options.length > 1) parts.push(v);
        }
      });
      return parts.join(' | ');
    }

    if (props) {
      const keys = Object.keys(props);
      keys.forEach(k => {
        const val = props[k];
        if (k.toLowerCase().includes('color')) parts.unshift(val);
        else if (k.toLowerCase().includes('type')) parts.push(`Plug ${val}`);
        else parts.push(val);
      });
      return parts.join(' | ');
    }

    if (variantOptions.length) {
      variantOptions.forEach(opt => {
        const lower = String(opt).toLowerCase();
        if (lower.includes('color')) parts.unshift(opt);
        else if (lower.includes('type')) parts.push(`Plug ${opt}`);
        else parts.push(opt);
      });
      return parts.join(' | ');
    }

    return '';
  }

  // Latest-wins queued quantity updates per item key
  scheduleQuantityUpdate(item, newQuantity, rootEl) {
    // Check if a global cart operation is in progress
    if (cartOperationManager.isInProgress()) {
      return;
    }

    const itemKey = item.key;
    const state = this.quantityUpdateState.get(itemKey) || { inFlight: false, pending: null };


    // Optimistic UI and disable controls immediately
    this.updateItemDisplayQuantityAndPrice(rootEl, item, Math.max(0, newQuantity));

    const start = async (qty) => {
      state.inFlight = true;
      this.setItemControlsDisabled(rootEl, true);
      try {
        if (qty <= 0) {
          await this.removeFromCart(item);
        } else {
          const cart = await updateCart(itemKey, qty);
          if (cart) {
            this.cart = cart;
            this.setStatus('loaded');
            // Partial update: totals + this item's display
            this.updateCartTotals(cart);
            const updatedItem = cart.items.find(ci => ci.key === itemKey);
            if (updatedItem) {
              this.updateItemDisplayQuantityAndPrice(rootEl, updatedItem, updatedItem.quantity);
            } else {
              // Fallback if we cannot find the item (rare): full re-render
              this.renderCart(cart);
            }
          }
        }
      } catch (err) {
        console.error('Failed to update quantity:', err);
      } finally {
        state.inFlight = false;
        const next = state.pending;
        state.pending = null;
        this.setItemControlsDisabled(rootEl, false);
        if (typeof next === 'number' && next !== qty) {
          // Start the queued latest update
          start(next);
        }
      }
    };

    if (state.inFlight) {
      state.pending = newQuantity;
    } else {
      start(newQuantity);
    }

    this.quantityUpdateState.set(itemKey, state);
  }

  setItemControlsDisabled(rootEl, disabled) {
    rootEl?.setAttribute('aria-busy', disabled ? 'true' : 'false');
    rootEl?.querySelectorAll('button, input').forEach(el => {
      el.disabled = !!disabled;
    });
  }

  updateItemDisplayQuantityAndPrice(rootEl, item, quantity) {
    const qtyInput = rootEl?.querySelector('.cart-item--quantity input');
    if (qtyInput) qtyInput.value = Math.max(0, parseInt(quantity || 0, 10));

    const itemPrice = rootEl?.querySelector('.cart-item--price');
    const priceFinal = itemPrice?.querySelector('.cart-item--price-value');
    const priceDiscount = itemPrice?.querySelector('.cart-item--price-discount');
    if (priceFinal) {
      if (item.original_price !== item.final_price && priceDiscount) {
        priceDiscount.innerHTML = this.getPriceFormat(item.original_price * quantity);
        priceFinal.innerHTML = this.getPriceFormat(item.final_price * quantity);
      } else {
        priceFinal.innerHTML = this.getPriceFormat(item.price * quantity);
      }
    }
  }

  updateCartTotals(cart) {
    const count = cart?.item_count || 0;
    if (this.cartTotalItems) {
      this.cartTotalItems.innerHTML = `${count} item${count === 1 ? '' : 's'} in cart`;
    }
    if (this.cartTotalPrice) {
      this.cartTotalPrice.innerHTML = `<span>Subtotal</span><span>${this.getPriceFormat(cart?.total_price || 0)}</span>`;
    }
    this.updateEmptyState(cart);
  }

  scrollToTopItems() {
    // Smooth scroll to top of cart items container
    if (this.cartItems) {
      this.cartItems.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  // Combined Listings helpers
  isCombinedListingChild(item) {
    const hasOnlyColorVariant = Array.isArray(item?.options_with_values)
      && item.options_with_values.length === 1
      && String(item.options_with_values[0]?.name || '').toLowerCase().includes('color');
    const title = String(item?.product_title || '').toLowerCase();
    const titleContainsPlugType = ['type a','type e','type i', 'type g'].some(t => title.includes(t));
    return hasOnlyColorVariant && titleContainsPlugType;
  }

  formatCombinedListingChildProperties(item) {
    const parts = [];
    if (Array.isArray(item?.options_with_values) && item.options_with_values.length) {
      const color = item.options_with_values[0];
      if (color?.name && String(color.name).toLowerCase().includes('color')) {
        parts.push(color.value);
      }
    }
    const title = String(item?.product_title || '').toLowerCase();
    if (title.includes('type a')) parts.push('Plug Type A');
    else if (title.includes('type e')) parts.push('Plug Type E');
    else if (title.includes('type i')) parts.push('Plug Type I');
    else if (title.includes('type g')) parts.push('Plug Type G');
    return parts.join(' | ');
  }

  async handleAddToCartTrigger(e) {
    const raw = e?.currentTarget?.dataset?.customAtc || e?.target?.dataset?.customAtc;
    if (!raw) return;
    const ids = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (!ids.length) return;
    const items = ids.map(id => ({ id, quantity: 1 }));
    await addToCartMultiple({ items });
  }

  setAllCartButtonsDisabled(disabled) {
    // Disable/enable all cart quantity buttons
    const quantityButtons = this.querySelectorAll('.cart-item--quantity-increment, .cart-item--quantity-decrement');
    quantityButtons.forEach(button => {
      button.disabled = disabled;
    });

    // Disable/enable all quantity inputs
    const quantityInputs = this.querySelectorAll('.cart-item--quantity input');
    quantityInputs.forEach(input => {
      input.disabled = disabled;
    });

    // Disable/enable all dynamic ATC buttons
    const dynamicAtcButtons = document.querySelectorAll('dynamic-atc button, .variant-atc, .dynamic-atc');
    dynamicAtcButtons.forEach(button => {
      button.disabled = disabled;
      if (disabled) {
        button.classList.add('disabled');
      } else {
        button.classList.remove('disabled');
      }
    });

    // Disable/enable all other ATC buttons
    const atcButtons = document.querySelectorAll('[data-custom-atc], .btn--atc, .add-to-cart-btn');
    atcButtons.forEach(button => {
      button.disabled = disabled;
      if (disabled) {
        button.classList.add('disabled');
      } else {
        button.classList.remove('disabled');
      }
    });
  }
}


// let the browser know that <my-element> is served by our new class
customElements.define("mini-cart", Cart);